// src/client/api.js
import { resetIdleTimer } from "@/utils/idleTimer.js";

let logoutHandler = null;

/* ============================================================
   Register global logout callback (from useAuth.js)
============================================================ */
export function registerLogoutHandler(fn) {
  logoutHandler = fn;
}

/* ============================================================
   Check if current route is login page (lazy router import)
============================================================ */
async function isOnLoginPage() {
  const { default: router } = await import("@/router");
  return router.currentRoute.value.path === "/login";
}

/* ============================================================
   Handle unauthorized responses (401 / 403)
============================================================ */
async function handleUnauthorizedResponse() {
  const { default: router } = await import("@/router");

  const onLoginPage = router.currentRoute.value.path === "/login";
  if (onLoginPage) {
    // On login page: 401 means "wrong password" → do nothing
    return;
  }

  // Trigger global logout handler (from useAuth.js)
  if (logoutHandler) {
    try {
      await logoutHandler();
    } catch (err) {
      console.error("Logout handler error:", err);
    }
  }

  // Ensure redirect only if not already there
  if (router.currentRoute.value.path !== "/login") {
    router.push("/login");
  }
}

/* ============================================================
   Main wrapper for ALL authenticated API calls
============================================================ */
export async function authFetch(url, options = {}) {
  // Any API call counts as activity → reset idle timer
  resetIdleTimer();

  let response;

  try {
    response = await fetch(url, {
      ...options,
      credentials: "include", // REQUIRED: send httpOnly cookies
    });
  } catch (err) {
    // Network error (server down, no internet, CORS, etc.)
    console.error("Network error in authFetch:", err);
    throw err;
  }

  // Unauthorized session → handle globally
  if (response.status === 401 || response.status === 403) {
    await handleUnauthorizedResponse();
    throw new Error("Unauthorized");
  }

  return response;
}
